<template>
	<auth-layout
		headline="创建您的账号"
		subtext="创建账号后即可开始消息协作。"
		mobile-desc="账号注册"
		:hide-mobile-brand-card="true"
		:badges="[
			{ label: '快速', text: '注册即用，无需繁琐的审核流程' },
			{ label: '安全', text: '端到端加密通信，数据安全有保障' }
		]"
	>
		<template #preview>
			<div class="preview-card">
				<div class="preview-step">
					<div class="preview-step-icon"><i class="el-icon-user"></i></div>
					<div class="preview-step-body">
						<div class="preview-step-label">创建账号</div>
						<div class="preview-step-bar"><div class="preview-step-fill" style="width:100%"></div></div>
					</div>
					<i class="el-icon-check preview-step-done"></i>
				</div>
				<div class="preview-step">
					<div class="preview-step-icon"><i class="el-icon-lock"></i></div>
					<div class="preview-step-body">
						<div class="preview-step-label">设置密码</div>
						<div class="preview-step-bar"><div class="preview-step-fill" style="width:100%"></div></div>
					</div>
					<i class="el-icon-check preview-step-done"></i>
				</div>
				<div class="preview-step preview-step--active">
					<div class="preview-step-icon preview-step-icon--active"><i class="el-icon-chat-dot-round"></i></div>
					<div class="preview-step-body">
						<div class="preview-step-label">开始聊天</div>
						<div class="preview-step-bar"><div class="preview-step-fill preview-step-fill--active" style="width:60%"></div></div>
					</div>
				</div>
				<div class="preview-status">
					<i class="el-icon-success"></i> 注册即可立即使用
				</div>
			</div>
		</template>

		<!-- 注册卡片 -->
		<div class="auth-card auth-card--register-compact">
			<div class="card-header">
				<div class="card-title-row">
					<div class="card-title">注册</div>
					<router-link class="register-switch-link" to="/login">去登录</router-link>
				</div>
			</div>
			<!-- 注册方式 Tab -->
			<div class="auth-tabs" v-if="config.mode && config.mode.length > 1">
				<div v-for="mode in displayModes" :key="mode"
					class="tab-item" :class="{ active: dataForm.mode === mode }" @click="switchMode(mode)">
					<i :class="mode === 'phone' ? 'el-icon-mobile-phone' : 'el-icon-user'"></i>
					{{ modeNameMap[mode] }}
				</div>
			</div>
			<div class="card-content">
				<el-form :model="dataForm" status-icon :rules="rules" ref="registerForm">
					<el-form-item v-if="dataForm.mode == 'phone'" prop="phone">
						<label class="form-label">手机号码</label>
						<el-input v-model="dataForm.phone" placeholder="请输入7-15位国际手机号" prefix-icon="el-icon-mobile-phone"
							maxlength="16"></el-input>
					</el-form-item>
					<el-form-item v-show="dataForm.mode == 'phone'" prop="code">
						<label class="form-label">验证码</label>
						<div class="send-code-bar">
							<el-input class="input" v-model="dataForm.code" placeholder="请输入6位短信验证码" maxlength="6"
								prefix-icon="el-icon-key"></el-input>
							<div class="lock-text" v-if="phoneLockTime > 0">{{ `${phoneLockTime}s` }}</div>
							<el-button v-else class="code-btn" type="primary" plain size="mini"
								@click="onSendSmsCode">获取验证码</el-button>
						</div>
					</el-form-item>
					<el-form-item prop="userName">
						<label class="form-label">用户名</label>
						<el-input v-model="dataForm.userName" maxlength="15" placeholder="请输入7-15位字母或数字账号" show-word-limit
							prefix-icon="el-icon-user"></el-input>
					</el-form-item>
					<el-form-item prop="password">
						<label class="form-label">密码</label>
						<el-input :type="isShowPwd ? 'text' : 'password'" v-model="dataForm.password" maxlength="20"
							placeholder="请输入5-20位字母或数字密码" prefix-icon="el-icon-lock">
							<template #suffix>
								<i :class="isShowPwd ? 'icon-pwd-show' : 'icon-pwd-hide'" @click="switchPassword"
									class="iconfont password-switch-icon"></i>
							</template>
						</el-input>
					</el-form-item>
					<el-form-item v-if="dataForm.mode !== 'phone'" prop="confirmPassword">
						<label class="form-label">确认密码</label>
						<el-input :type="isShowConfirmPwd ? 'text' : 'password'" v-model="dataForm.confirmPassword"
							maxlength="20" placeholder="请再次输入5-20位字母或数字密码" prefix-icon="el-icon-lock">
							<template #suffix>
								<i :class="isShowConfirmPwd ? 'icon-pwd-show' : 'icon-pwd-hide'"
									@click="switchConfirmPassword" class="iconfont password-switch-icon"></i>
							</template>
						</el-input>
					</el-form-item>
					<el-button class="submit-btn" type="primary" :loading="submitting" :disabled="submitting"
						@click="submitForm()">注册</el-button>
				</el-form>
			</div>
		</div>

		<template #extra>
			<captcha-image ref="captchaRef"></captcha-image>
		</template>
	</auth-layout>
</template>

<script>
import { saveAuthSession } from '../api/authSession';
import AuthLayout from '../components/common/AuthLayout.vue';
import CaptchaImage from '../components/common/CaptchaImage.vue';
import loginCredentialStore from '../api/loginCredentialStore';
import { getStableDeviceId } from '../utils/deviceId';
import { syncAuthWindow } from '../utils/authWindow';
import {
	trimOuterWhitespace,
	sanitizeAlphaNumeric,
	validatePhone,
	validateRegisterUsername,
	validatePassword,
	validateSmsCode,
	createConfirmPasswordValidator
} from '../utils/authValidators';
import pkg from '../../package.json';

const MODE_ORDER = {
	username: 0,
	phone: 1
};
const LAST_LOGIN_USER_KEY = 'loginLastUserByTenant';

export default {
	name: "login",
	components: {
		AuthLayout,
		CaptchaImage
	},
	data() {
		return {
			dataForm: {
				phone: '',
				code: '',
				mode: 'username',
				userName: '',
				password: '',
				confirmPassword: ''
			},
			isShowPwd: false,
			isShowConfirmPwd: false,
			submitting: false,
			rules: {
				phone: [{
					validator: validatePhone,
					trigger: 'submit'
				}],
				code: [{
					validator: (rule, value, callback) => {
						if (this.dataForm.mode !== 'phone') {
							callback();
							return;
						}
						validateSmsCode(rule, value, callback);
					},
					trigger: 'submit'
				}],
				userName: [{
					validator: validateRegisterUsername,
					trigger: 'submit'
				}],
				password: [{
					validator: validatePassword,
					trigger: 'submit'
				}],
				confirmPassword: [{
					validator: (rule, value, callback) => {
						if (this.dataForm.mode === 'phone') {
							callback();
							return;
						}
						createConfirmPasswordValidator(() => this.dataForm.password)(rule, value, callback);
					},
					trigger: 'submit'
				}]
			},
			showCaptchaImage: false,
			phoneLockTime: 0,
			phoneLockTimer: null,
			modeNameMap: {
				username: "用户名",
				phone: "手机号"
			}
		};
	},
	methods: {
		getCredentialApi() {
			if (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.loginCredential) {
				return {
					save: (payload) => window.electronAPI.loginCredential.save(payload)
				};
			}
			return loginCredentialStore;
		},
		getCurrentTenantKey() {
			const endpoint = this.$tenantEndpoint.getCurrentSession();
			const rememberedTenantKey = this.$tenantEndpoint.getRememberedTenantKey();
			return (endpoint && endpoint.tenantKey) || rememberedTenantKey || '';
		},
		readRememberedUserMap() {
			try {
				const cached = localStorage.getItem(LAST_LOGIN_USER_KEY);
				const parsed = JSON.parse(cached || '{}');
				return parsed && typeof parsed === 'object' ? parsed : {};
			} catch (error) {
				return {};
			}
		},
		rememberUserName(tenantKey, userName) {
			if (!tenantKey) {
				return;
			}
			const normalizedUserName = typeof userName === 'string' ? userName.trim() : '';
			const rememberedMap = this.readRememberedUserMap();
			if (normalizedUserName) {
				rememberedMap[tenantKey] = normalizedUserName;
				localStorage.setItem('username', normalizedUserName);
			} else {
				delete rememberedMap[tenantKey];
				localStorage.removeItem('username');
			}
			localStorage.setItem(LAST_LOGIN_USER_KEY, JSON.stringify(rememberedMap));
		},
		async persistCredentialRecord({ tenantKey, userName, password }) {
			if (!tenantKey || !userName) {
				return;
			}
			await this.getCredentialApi().save({
				tenantKey,
				userName,
				password,
				source: this.configStore.electronMode ? 'electron' : 'browser'
			});
			this.rememberUserName(tenantKey, userName);
		},
		normalizeRegisterForm() {
			this.dataForm.phone = trimOuterWhitespace(this.dataForm.phone);
			this.dataForm.code = trimOuterWhitespace(this.dataForm.code);
			this.dataForm.userName = trimOuterWhitespace(this.dataForm.userName);
			this.dataForm.password = trimOuterWhitespace(this.dataForm.password);
			this.dataForm.confirmPassword = trimOuterWhitespace(this.dataForm.confirmPassword);
		},
		buildAutoLoginPayload(deviceId) {
			return {
				terminal: this.resolveTerminal(),
				userName: this.dataForm.mode === 'phone' ? this.dataForm.phone : this.dataForm.userName,
				password: this.dataForm.password,
				deviceId,
				deviceType: this.resolveDeviceType()
			};
		},
		submitForm() {
			if (this.submitting) {
				return;
			}
			this.normalizeRegisterForm();
			if (this.config.mode && this.config.mode.length && !this.config.mode.includes(this.dataForm.mode)) {
				this.$message.error("当前注册方式已关闭");
				return;
			}
			this.$refs.registerForm.validate(async (valid) => {
				if (!valid) {
					return;
				}
				this.submitting = true;
				try {
					const deviceId = await getStableDeviceId();
					const requestData = {
						...this.dataForm,
						deviceId
					};
					const autoLoginPayload = this.buildAutoLoginPayload(deviceId);
					delete requestData.confirmPassword;
					await this.$http({
						url: "/register",
						method: 'post',
						data: requestData
					});
					await this.autoLogin(autoLoginPayload);
				} finally {
					this.submitting = false;
				}
			});
		},
		async autoLogin(loginPayload) {
			const { terminal, userName, password, deviceId, deviceType } = loginPayload;
			const appVersion = await this.getAppVersion();
			const data = await this.$http({
				url: "/login",
				method: 'post',
				data: {
					terminal,
					userName,
					password,
					deviceId,
					deviceType,
					appVersion
				}
			});
			const tenantKey = this.getCurrentTenantKey();
			localStorage.setItem('isAutoLogin', JSON.stringify(true));
			localStorage.setItem('username', userName);
			await this.persistCredentialRecord({
				tenantKey,
				userName,
				password
			});
			localStorage.removeItem('password');
				saveAuthSession(data);
			await this.$router.push("/home/chat");
			this.dataForm.code = '';
			this.dataForm.password = '';
			this.dataForm.confirmPassword = '';
			this.$message.success('注册成功');
		},
		resolveTerminal() {
			return this.configStore.electronMode ? this.$enums.TERMINAL_TYPE.PC : this.$enums.TERMINAL_TYPE.WEB;
		},
		resolveDeviceType() {
			return this.configStore.electronMode ? 'pc' : 'web';
		},
		getAppVersion() {
			if (window.electronAPI && window.electronAPI.invoke) {
				return window.electronAPI.invoke('get-app-version').catch(() => process.env.VUE_APP_VERSION || pkg.version || '');
			}
			return Promise.resolve(process.env.VUE_APP_VERSION || pkg.version || '');
		},
		onSendSmsCode() {
			this.normalizeRegisterForm();
			this.$refs.registerForm.validateField('phone', (valid) => {
				if (valid == '') {
					this.$refs.captchaRef.open((id, code) => {
						this.phoneLockTime = 60;
						this.phoneLockTimer && clearInterval(this.phoneLockTimer);
						this.phoneLockTimer = setInterval(() => {
							this.phoneLockTime -= 1;
							if (this.phoneLockTime <= 0) {
								this.phoneLockTimer && clearInterval(this.phoneLockTimer);
							}
						}, 1000)
						let data = {
							phone: this.dataForm.phone,
							id: id,
							code: code
						}
						this.$http({
							url: "/captcha/sms/code",
							method: 'post',
							data: data
						}).then(() => {
							this.$message.success("验证码已发送至您的手机，请注意查收")
						})
					});
				}
			})
		},
		switchMode(mode) {
			if (!mode || this.dataForm.mode === mode) {
				return;
			}
			this.$refs.registerForm && this.$refs.registerForm.resetFields();
			this.dataForm.mode = mode;
		},
		syncModeWithConfig() {
			if (!this.displayModes.length) {
				return;
			}
			if (this.displayModes.includes(this.dataForm.mode)) {
				return;
			}
			this.switchMode(this.displayModes[0]);
		},
		switchPassword() {
			this.isShowPwd = !this.isShowPwd;
		},
		switchConfirmPassword() {
			this.isShowConfirmPwd = !this.isShowConfirmPwd;
		}
	},
	computed: {
		displayModes() {
			return [...(this.config.mode || [])].sort((prev, next) => {
				const prevOrder = MODE_ORDER[prev] ?? Number.MAX_SAFE_INTEGER;
				const nextOrder = MODE_ORDER[next] ?? Number.MAX_SAFE_INTEGER;
				return prevOrder - nextOrder;
			});
		},
		config() {
			const registration = this.configStore.globalConfig?.registration || {};
			const mode = (registration.mode || []).filter((item) => item !== 'email');
			return {
				...registration,
				mode
			};
		},
	},
	watch: {
		config: {
			handler() {
				this.syncModeWithConfig();
			}
		},
		'dataForm.phone'(value) {
			const normalized = trimOuterWhitespace(value);
			if (normalized !== value) {
				this.dataForm.phone = normalized;
			}
		},
		'dataForm.code'(value) {
			const normalized = trimOuterWhitespace(value);
			if (normalized !== value) {
				this.dataForm.code = normalized;
			}
		},
		'dataForm.userName'(value) {
			const normalized = sanitizeAlphaNumeric(trimOuterWhitespace(value));
			if (normalized !== value) {
				this.dataForm.userName = normalized;
			}
		},
		'dataForm.password'(value) {
			const normalized = trimOuterWhitespace(value);
			if (normalized !== value) {
				this.dataForm.password = normalized;
			}
		},
		'dataForm.confirmPassword'(value) {
			const normalized = trimOuterWhitespace(value);
			if (normalized !== value) {
				this.dataForm.confirmPassword = normalized;
			}
		}
	},
	mounted() {
		this.syncModeWithConfig();
		this.configStore.loadConfig().catch((error) => {
			console.error('加载系统配置失败:', error);
		});
		getStableDeviceId();
		syncAuthWindow();
	}
}
</script>

<style scoped lang="scss">
@import "../assets/style/auth-card";
@import "../assets/style/auth-preview";

.send-code-bar {
	display: flex;
	align-items: center;
	gap: 4px;

	.input { flex: 1; min-width: 0; }

	.lock-text {
		font-size: 12px;
		color: var(--im-text-color-light);
		white-space: nowrap;
		flex-shrink: 0;
	}

	.code-btn {
		height: 32px;
		border-radius: 8px;
		font-size: 12px;
		padding: 0 12px;
		white-space: nowrap;
	}
}

::v-deep .auth-card--register-compact .card-title-row {
	align-items: center;
}

::v-deep .auth-card--register-compact .register-switch-link {
	flex-shrink: 0;
	font-size: 12px;
	color: var(--im-color-primary);
	text-decoration: none;
}

::v-deep .auth-card--register-compact .card-header {
	padding: 14px 16px 0;
}

::v-deep .auth-card--register-compact .card-content {
	padding: 10px 16px 14px;
}

::v-deep .auth-card--register-compact .auth-tabs {
	padding: 8px 16px 0;
	gap: 4px;
}

::v-deep .auth-card--register-compact .tab-item {
	padding: 6px 0;
	font-size: 12px;
	border-radius: 8px;
}

::v-deep .auth-card--register-compact .tab-item i {
	font-size: 12px;
}

::v-deep .auth-card--register-compact .el-form-item {
	margin-bottom: 6px;
}

::v-deep .auth-card--register-compact .form-label {
	font-size: 12px;
	margin-bottom: 2px;
}

::v-deep .auth-card--register-compact .el-input__inner {
	height: 36px;
	line-height: 36px;
}

::v-deep .auth-card--register-compact .submit-btn {
	height: 36px;
	margin-top: 2px;
}

.password-switch-icon {
	cursor: pointer;
	color: var(--im-text-color-light);
	font-size: 16px;
	padding: 0 4px;
}

// 预览插图
.preview-step {
	display: flex;
	align-items: center;
	gap: 10px;

	&--active .preview-step-label {
		color: var(--im-color-primary);
		font-weight: 500;
	}
}

.preview-step-icon {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: var(--im-background-active);
	display: grid;
	place-items: center;
	font-size: 14px;
	color: var(--im-text-color-light);
	flex-shrink: 0;

	&--active {
		background: var(--im-color-primary);
		color: #fff;
	}
}

.preview-step-body { flex: 1; min-width: 0; }

.preview-step-label {
	font-size: 13px;
	color: var(--im-text-color);
	margin-bottom: 4px;
}

.preview-step-bar {
	height: 3px;
	border-radius: 2px;
	background: rgba(var(--im-black-rgb), 0.06);
	overflow: hidden;
}

.preview-step-fill {
	height: 100%;
	border-radius: 2px;
	background: var(--im-text-color-light);
	opacity: 0.4;

	&--active {
		background: var(--im-color-primary);
		opacity: 1;
	}
}

.preview-step-done {
	font-size: 14px;
	color: var(--im-color-success, #67c23a);
	flex-shrink: 0;
}

</style>
